/**
 * View Components for PhotoCollection2 Test Page
 *
 * Components for rendering the main test views (collection and photo viewer).
 */

import { PhotoCollection2 } from '../../components/PhotoCollection2';
import { CollectionAndDetailView2 } from '../../components/CollectionAndDetailView2';
import { PhotoViewer } from '../../components/composed/PhotoViewer';
import { Text } from '../../components/atoms/Text';
import type { DetailLocation } from '../../components/composed/CollectionControlPanel';
import type { TemplateName, PhotoItem2ViewModel } from '../../components/PhotoItem2/PhotoItem2.types';
import type { LayoutName, SelectionMode, ReorderResult } from '../../components/PhotoCollection2/PhotoCollection2.types';
import type { Photo } from '../../types/photo';
import {
  SampleFixedHeader,
  SampleScrollHeader,
  SampleScrollFooter,
  SampleFixedFooter,
} from './SampleSlots';

/**
 * CollectionComponentView - Renders PhotoCollection2 or CollectionAndDetailView2
 */
export interface CollectionComponentViewProps {
  photos: PhotoItem2ViewModel[];
  layout: LayoutName;
  itemTemplate: TemplateName;
  zoom: number;
  selectionMode: SelectionMode;
  selectedIds: string[];
  detailLocation: DetailLocation;
  reorderMode: boolean;
  // Header/Footer slot controls (#102)
  showFixedHeader: boolean;
  showScrollHeader: boolean;
  showScrollFooter: boolean;
  showFixedFooter: boolean;
  onSelectionChange: (ids: string[]) => void;
  onReorder: (result: ReorderResult) => void;
}

export function CollectionComponentView({
  photos,
  layout,
  itemTemplate,
  zoom,
  selectionMode,
  selectedIds,
  detailLocation,
  reorderMode,
  showFixedHeader,
  showScrollHeader,
  showScrollFooter,
  showFixedFooter,
  onSelectionChange,
  onReorder,
}: CollectionComponentViewProps) {
  // When using PhotoCollection2 directly (no detail panel), pass slot props directly
  // Note: PhotoCollection2 internally handles layout compatibility - it will log a warning and filter scroll slots
  // (not fixed slots) for unsupported layouts (e.g., horizontal)
  if (detailLocation === 'none') {
    return (
      <PhotoCollection2
        className="w-full h-full"
        photos={photos}
        layout={layout}
        itemTemplate={itemTemplate}
        zoom={zoom}
        selectionMode={selectionMode}
        selectedIds={selectedIds}
        onSelectionChange={onSelectionChange}
        reorderMode={reorderMode}
        onReorder={onReorder}
        fixedHeader={showFixedHeader ? <SampleFixedHeader /> : undefined}
        scrollHeader={showScrollHeader ? <SampleScrollHeader photoCount={photos.length} /> : undefined}
        scrollFooter={showScrollFooter ? <SampleScrollFooter photoCount={photos.length} /> : undefined}
        fixedFooter={showFixedFooter ? <SampleFixedFooter selectedCount={selectedIds.length} /> : undefined}
      />
    );
  }

  // When using CollectionAndDetailView2, use compound component pattern
  return (
    <CollectionAndDetailView2
      className="w-full h-full"
      photos={photos}
      collectionPosition={
        detailLocation === 'top' ? 'bottom' :
        detailLocation === 'bottom' ? 'top' :
        detailLocation === 'left' ? 'right' :
        'left'
      }
      layout={layout}
      itemTemplate={itemTemplate}
      zoom={zoom}
      selectionMode={selectionMode}
      selectedIds={selectedIds}
      onSelectionChange={onSelectionChange}
    >
      {showFixedHeader && (
        <CollectionAndDetailView2.Header position="fixed">
          <SampleFixedHeader />
        </CollectionAndDetailView2.Header>
      )}
      {showScrollHeader && (
        <CollectionAndDetailView2.Header position="scroll">
          <SampleScrollHeader photoCount={photos.length} />
        </CollectionAndDetailView2.Header>
      )}
      {showScrollFooter && (
        <CollectionAndDetailView2.Footer position="scroll">
          <SampleScrollFooter photoCount={photos.length} />
        </CollectionAndDetailView2.Footer>
      )}
      {showFixedFooter && (
        <CollectionAndDetailView2.Footer position="fixed">
          <SampleFixedFooter selectedCount={selectedIds.length} />
        </CollectionAndDetailView2.Footer>
      )}
    </CollectionAndDetailView2>
  );
}

/**
 * PhotoViewerComponentView - Renders PhotoViewer
 */
export interface PhotoViewerComponentViewProps {
  photo: Photo | null;
}

export function PhotoViewerComponentView({
  photo,
}: PhotoViewerComponentViewProps) {
  if (!photo) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Text className="text-muted">No photos available. Switch to Real data source.</Text>
      </div>
    );
  }

  return (
    <div className="w-full h-full border border-border rounded-lg overflow-hidden">
      {/* Force remount on photo change to reset zoom/pan state */}
      <PhotoViewer key={photo.id} photo={photo} className="w-full h-full" />
    </div>
  );
}
